import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { register } from "../api/auth";
import { colors, spacing, borderRadius, typography, shadows, createButtonStyle } from "../styles/theme";

// Minimum 8 znaków, możesz ustawić inne wymagania
const MIN_PASSWORD_LENGTH = 8;

// Prosta funkcja sprawdzająca email (lepsza niż tylko required)
const validateEmail = (email: string) => {
  return /\S+@\S+\.\S+/.test(email);
};

const RegisterForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Prosty sanity-check: sprawdzamy czy dane są poprawne
  const isValid =
    validateEmail(email) &&
    password.length >= MIN_PASSWORD_LENGTH &&
    password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validateEmail(email)) {
      setError("Podaj poprawny adres email.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków.`);
      return;
    }
    if (password !== confirm) {
      setError("Hasła nie są takie same.");
      return;
    }
    try {
      await register(email, password);
      setSuccess(true);
      setTimeout(() => {
        navigate("/waiting-activation");
      }, 1500);
    } catch (err: any) {
      if (err?.response?.data?.detail) {
        setError(`Błąd: ${err.response.data.detail}`);
      } else if (err?.response?.status === 409) {
        setError("Ten email jest już zajęty.");
      } else {
        setError("Błąd rejestracji. Upewnij się, że email nie jest już zajęty.");
      }
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)`,
        padding: spacing.xl,
      }}>
        <div style={{
          maxWidth: 420,
          width: "100%",
          background: colors.background,
          borderRadius: borderRadius.lg,
          boxShadow: shadows.xl,
          padding: spacing.xxxl,
          textAlign: "center",
        }}>
          <div style={{
            fontSize: 64,
            marginBottom: spacing.lg,
          }}>
            ✅
          </div>
          <h2 style={{
            fontSize: typography.fontSize.xxl,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            marginBottom: spacing.md,
          }}>
            Konto utworzone!
          </h2>
          <p style={{
            fontSize: typography.fontSize.base,
            color: colors.textSecondary,
            lineHeight: 1.6,
          }}>
            Oczekuj na aktywację przez administratora...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: `linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)`,
      padding: spacing.xl,
    }}>
      <div style={{
        maxWidth: 420,
        width: "100%",
        background: colors.background,
        borderRadius: borderRadius.lg,
        boxShadow: shadows.xl,
        padding: spacing.xxxl,
      }}>
        {/* Logo SEMTOP */}
        <div style={{
          textAlign: "center",
          marginBottom: spacing.xl,
        }}>
          <img
            src="/SEMTOP_Agencja_Marketingowa_Katowice.webp"
            alt="SEMTOP Logo"
            style={{
              width: 200,
              height: "auto",
              marginBottom: spacing.md,
            }}
          />
          <h1 style={{
            fontSize: typography.fontSize.xxxl,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            marginBottom: spacing.xs,
          }}>
            Semtop Faktury
          </h1>
          <p style={{
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Utwórz nowe konto
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: "block",
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              marginBottom: spacing.xs,
            }}>
              Email
            </label>
            <input
              type="email"
              placeholder="twoj@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: spacing.md,
                fontSize: typography.fontSize.base,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: spacing.lg }}>
            <label style={{
              display: "block",
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              marginBottom: spacing.xs,
            }}>
              Hasło (min. {MIN_PASSWORD_LENGTH} znaków)
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              style={{
                width: "100%",
                padding: spacing.md,
                fontSize: typography.fontSize.base,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Confirm Password */}
          <div style={{ marginBottom: spacing.xl }}>
            <label style={{
              display: "block",
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
              color: colors.textPrimary,
              marginBottom: spacing.xs,
            }}>
              Powtórz hasło
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              style={{
                width: "100%",
                padding: spacing.md,
                fontSize: typography.fontSize.base,
                border: `1px solid ${colors.border}`,
                borderRadius: borderRadius.md,
                outline: "none",
                transition: "all 0.2s ease",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${colors.primaryLight}`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = colors.border;
                e.target.style.boxShadow = "none";
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isValid}
            style={{
              ...createButtonStyle('primary', !isValid),
              width: "100%",
              padding: `${spacing.md}px ${spacing.lg}px`,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
            }}
          >
            Zarejestruj się
          </button>

          {/* Error Message */}
          {error && (
            <div style={{
              marginTop: spacing.lg,
              padding: spacing.md,
              background: colors.errorLight,
              color: colors.error,
              border: `1px solid ${colors.error}`,
              borderRadius: borderRadius.md,
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.semibold,
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* Login Link */}
          <div style={{
            marginTop: spacing.lg,
            textAlign: "center",
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Masz już konto?{" "}
            <Link
              to="/login"
              style={{
                color: colors.primary,
                fontWeight: typography.fontWeight.semibold,
                textDecoration: "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
            >
              Zaloguj się
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
